import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { selectLineItemsByProduct } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { orchestratePnARefresh } from "~/features/cart/cart/redux/actions/cartRefresh";
import { cartLoadErrorAction, cartLoadFinishAction, cartLoadStartAction } from "~/features/cart/cart/redux/actions";
import { emptyCartData, getActiveCartData } from "~/features/cart/cart/api/CartView";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "~/app/store";
import { type AnyAction } from "~/app/store/types";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { type TransformedCartData } from "~/features/cart/cart/transformers/CartResponseTransformer";
import { trackCartContents } from "~/app/analytics/ga/abandonment";
import { TranslationType } from "~/common/types/TranslationModel";
import { loadCartPna, quotePackCodeFromEntries } from "~/features/cart/cart/pna/cartPna";
import * as pnaUpdateAccumulator from "./pnaUpdateAccumulator";
import { resolveActiveCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
type AsyncThunkResult<R> = ThunkAction<Promise<R>, RootState, undefined, AnyAction>;
function showAboveThresholdMessageAction() {
  return addMessageAdvanced("info", {
    value: "page.cart.message.abovePnaThreshold",
    type: TranslationType.INTERNAL,
    params: { count: getRuntimeConfig().cartPnaThreshold },
  });
}

function showBulkUploadSuccessMessageAction() {
  const params = new URLSearchParams(window.location.search);
  const count = +params.get("bulkSuccess")!;
  return addMessageAdvanced("info", {
    value: "cartPage.msg.successfullyAdded",
    type: TranslationType.INTERNAL,
    params: { count },
  });
}

function loadCartDataThunk(): AsyncThunkResult<TransformedCartData> {
  return async (dispatch) => {
    dispatch(cartLoadStartAction());

    try {
      const cartData = (await getActiveCartData()) ?? emptyCartData();
      dispatch(cartLoadFinishAction(cartData));
      return cartData;
    } catch (e) {
      console.error(e);
      dispatch(cartLoadErrorAction());
      throw e;
    }
  };
}

export function cartPageLoadThunk(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const data = await dispatch(loadCartDataThunk());

    if (userIsCustomer()) {
      const params = new URLSearchParams(window.location.search);
      if (params.has("bulkSuccess")) {
        dispatch(showBulkUploadSuccessMessageAction());
      }

      if (Object.values(data.packageEntriesById).length <= getRuntimeConfig().cartPnaThreshold) {
        dispatch(performCartRefresh());
      } else {
        dispatch(showAboveThresholdMessageAction());
      }

      trackCartContents(getState());
    }
  };
}

function performCartRefresh(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const lineItemsByProduct = selectLineItemsByProduct(getState());
    const activeCartId = await resolveActiveCartResourceId();
    const entries = Object.values(lineItemsByProduct);
    entries.sort((a, b) => (a[0].entryNumber ?? 0) - (b[0].entryNumber ?? 0));

    // Warmup: fire per-product PnA loads without awaiting. Each product's orchestration
    // awaits the SAME loadCartPna calls, and TanStack Query dedupes them into one transport
    // call per unique (productCode, priceReference, packCode) triple.
    const products = getState().products;
    for (const lineItems of entries) {
      const productCode = lineItems[0].productCode;
      const base = products.basic[productCode];
      const packCode = quotePackCodeFromEntries(lineItems);
      const priceReferences = new Set(lineItems.map((lineItem) => lineItem.priceReference));
      for (const priceReference of priceReferences) {
        void loadCartPna({ productCode, priceReference, base, packCode, activeCartId }).catch(() => undefined);
      }
    }

    const gen = pnaUpdateAccumulator.activate(dispatch);
    entries.forEach((lineItems) => dispatch(orchestratePnARefresh(lineItems, gen, activeCartId)));
  };
}
